import { useEffect, useState } from 'react'

import { useLingui } from '@lingui/react'
import { useFormContext } from 'react-hook-form'

import type { I18nLanguage } from '@twisto/base'
import { useAppConfig } from '@twisto/base'
import {
  formatCurrencyNumber,
  parseFormattedCurrencyNumber,
} from '@twisto/utils'

import { CurrencyInputBase } from '../../atoms/currency-input-base'

import * as styles from './input-amount.styles'

type Props = {
  ariaLabel: string
  autoFocus?: boolean
  disabled?: boolean
  id?: string
  name: string
  max?: number
  min?: number
}

export const InputAmount = ({
  ariaLabel,
  autoFocus = false,
  disabled = false,
  id: nodeId,
  name,
  max,
  min,
}: Props) => {
  const { watch } = useFormContext()
  const { currencyCode } = useAppConfig()
  const { i18n } = useLingui()

  const locale = i18n.locale as I18nLanguage

  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (autoFocus) {
      setIsFocused(true)
    }
  }, [autoFocus])

  const amountValue = watch(name)

  const { currency: currencySign, position: currencyPosition } =
    parseFormattedCurrencyNumber(
      formatCurrencyNumber(locale, {
        currency: currencyCode,
      })(amountValue ?? 0)
    )

  const hasPrefix = currencyPosition === 'start' && !isFocused
  const hasSuffix = currencyPosition === 'end' && !isFocused

  const id = nodeId ?? name

  const handleFocus = () => {
    if (!isFocused) {
      setIsFocused(true)
    }
  }

  const handleBlur = () => {
    if (isFocused) {
      setIsFocused(false)
    }
  }

  return (
    <CurrencyInputBase
      aria-label={ariaLabel}
      autoFocus={autoFocus}
      css={(theme) => [styles.input(theme), disabled && styles.disabled(theme)]}
      data-testid="input-amount"
      displayType={disabled ? 'text' : 'input'}
      id={id}
      max={max}
      min={min}
      name={name}
      prefix={hasPrefix ? `${currencySign} ` : undefined}
      suffix={hasSuffix ? ` ${currencySign}` : undefined}
      onBlur={handleBlur}
      onFocus={handleFocus}
    />
  )
}
